import React, { useState } from 'react';
import { X, Plus, Trash2, Car, Calendar, Gauge, Fuel, AlertCircle, Check, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

const FUEL_TYPES = [
  { value: 'essence', label: 'Essence', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'diesel', label: 'Diesel', color: 'bg-gray-700 text-white' },
  { value: 'hybride', label: 'Hybride', color: 'bg-green-100 text-green-800' },
  { value: 'essence/électrique', label: 'Essence/électrique', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'diesel/électrique', label: 'Diesel/électrique', color: 'bg-teal-100 text-teal-800' },
  { value: 'essence/gpl', label: 'Essence / gaz combustible liquéfié (GPL)', color: 'bg-fuchsia-100 text-fuchsia-800' },
  { value: 'essence/éthanol', label: 'Essence/éthanol', color: 'bg-lime-100 text-lime-800' },
  { value: 'essence/éthanol/gaz', label: 'Essence / éthanol / gaz', color: 'bg-orange-100 text-orange-800' },
  { value: 'électrique', label: 'Électrique', color: 'bg-blue-100 text-blue-800' },
  { value: 'gpl', label: 'GPL', color: 'bg-purple-100 text-purple-800' },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 30 }, (_, i) => CURRENT_YEAR - i + 2);

const VehicleAddForm = ({ onClose, onSuccess, existingBrands = [], initialData = null }) => {
  const isEdit = !!initialData;
  const [step, setStep] = useState(isEdit ? 2 : 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    brand: initialData?.brand || '',
    isNewBrand: false,
    model: initialData?.model || '',
    years: initialData?.years ? [...initialData.years].sort((a, b) => b - a) : [],
    engines: initialData?.engines ? JSON.parse(JSON.stringify(initialData.engines)) : {}
  });
  
  // Engine form state
  const [currentPeriod, setCurrentPeriod] = useState({ start: CURRENT_YEAR - 5, end: CURRENT_YEAR });
  const [currentEngine, setCurrentEngine] = useState({
    fuel: 'essence',
    displacement: '',
    power: '',
    power_kw: '',
    engine_code: '',
    name: ''
  });

  // Handle brand selection
  const handleBrandChange = (value) => {
    if (value === '__NEW__') {
      setFormData({ ...formData, brand: '', isNewBrand: true });
    } else {
      setFormData({ ...formData, brand: value, isNewBrand: false });
    }
  };

  // Handle year selection
  const toggleYear = (year) => {
    const years = formData.years.includes(year)
      ? formData.years.filter(y => y !== year)
      : [...formData.years, year].sort((a, b) => b - a);
    setFormData({ ...formData, years });
  };

  // Select year range
  const selectYearRange = (start, end) => {
    const years = [];
    for (let y = start; y <= end; y++) {
      years.push(y);
    }
    setFormData({ ...formData, years: years.sort((a, b) => b - a) });
  };

  // Add engine to period
  const addEngine = () => {
    if (!currentEngine.name || !currentEngine.power) {
      setError('Le nom et la puissance sont obligatoires');
      return;
    }
    
    const periodKey = `${currentPeriod.start}-${currentPeriod.end}`;
    const newEngines = { ...formData.engines };
    
    if (!newEngines[periodKey]) {
      newEngines[periodKey] = [];
    }
    
    // Check for duplicate
    if (newEngines[periodKey].some(e => e.name === currentEngine.name)) {
      setError('Cette motorisation existe déjà pour cette période');
      return;
    }
    
    newEngines[periodKey].push({
      ...currentEngine,
      displacement: parseInt(currentEngine.displacement) || 0,
      power: parseInt(currentEngine.power),
      power_kw: parseInt(currentEngine.power_kw) || Math.round(parseInt(currentEngine.power) * 0.7355),
    });
    
    setFormData({ ...formData, engines: newEngines });
    setCurrentEngine({
      fuel: 'essence',
      displacement: '',
      power: '',
      power_kw: '',
      engine_code: '',
      name: ''
    });
    setError('');
  };

  // Remove engine
  const removeEngine = (periodKey, index) => {
    const newEngines = { ...formData.engines };
    newEngines[periodKey].splice(index, 1);
    if (newEngines[periodKey].length === 0) {
      delete newEngines[periodKey];
    }
    setFormData({ ...formData, engines: newEngines });
  };

  // Auto-calculate kW from ch
  const handlePowerChange = (value) => {
    const power = parseInt(value) || '';
    const power_kw = power ? Math.round(power * 0.7355) : '';
    setCurrentEngine({ ...currentEngine, power: value, power_kw: power_kw.toString() });
  };

  // Validate current step
  const validateStep = () => {
    setError('');
    
    if (step === 1) {
      if (!formData.brand.trim()) {
        setError('La marque est obligatoire');
        return false;
      }
      if (!formData.model.trim()) {
        setError('Le modèle est obligatoire');
        return false;
      }
      return true;
    }
    
    if (step === 2) {
      if (formData.years.length === 0) {
        setError('Sélectionnez au moins une année');
        return false;
      }
      return true;
    }
    
    if (step === 3) {
      if (Object.keys(formData.engines).length === 0) {
        setError('Ajoutez au moins une motorisation');
        return false;
      }
      return true;
    }
    
    return true;
  };

  // Handle next step
  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const vehicleData = {
        brand: formData.brand.toUpperCase().trim(),
        model: formData.model.trim(),
        years: formData.years,
        engines: formData.engines
      };
      
      await onSuccess(vehicleData);
      onClose();
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'ajout du véhicule');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get total engines count
  const getTotalEnginesCount = () => {
    return Object.values(formData.engines).reduce((sum, engines) => sum + engines.length, 0);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b bg-gradient-to-r from-orange-500 to-orange-600 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Car size={24} />
              <div>
                <h2 className="text-xl font-bold" data-testid="vehicle-form-title">
                  {isEdit ? `Modifier ${initialData.brand} ${initialData.model}` : 'Ajouter un véhicule'}
                </h2>
                <p className="text-orange-100 text-sm">Étape {step} sur 4</p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
              <X size={24} />
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="mt-4 flex gap-2">
            {[1, 2, 3, 4].map(s => (
              <div
                key={s}
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  s <= step ? 'bg-white' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {/* Step 1: Brand & Model */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Marque du véhicule *
                </label>
                {formData.isNewBrand ? (
                  <div className="flex gap-2">
                    <Input
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      placeholder="Entrez le nom de la marque"
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      onClick={() => setFormData({ ...formData, brand: '', isNewBrand: false })}
                    >
                      Annuler
                    </Button>
                  </div>
                ) : (
                  <select
                    value={formData.brand}
                    onChange={(e) => handleBrandChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Sélectionnez une marque</option>
                    {existingBrands.sort().map(brand => (
                      <option key={brand} value={brand}>{brand}</option>
                    ))}
                    <option value="__NEW__">➕ Ajouter une nouvelle marque</option>
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Modèle *
                </label>
                <Input
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="Ex: Clio V, Golf VIII, 308 III..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Incluez la génération si applicable (ex: III, IV, V)
                </p>
              </div>

              {formData.brand && formData.model && (
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                  <p className="text-sm text-gray-700">
                    Vous allez créer : <strong>{formData.brand.toUpperCase()} {formData.model}</strong>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Years */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Calendar size={18} />
                  Années de production *
                </label>
                <p className="text-xs text-gray-500 mb-4">
                  Sélectionnez toutes les années où ce modèle a été produit
                </p>
                
                {/* Quick select */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => selectYearRange(2020, CURRENT_YEAR)}
                  >
                    2020 - {CURRENT_YEAR}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => selectYearRange(2015, CURRENT_YEAR)}
                  >
                    2015 - {CURRENT_YEAR}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => selectYearRange(2010, CURRENT_YEAR)}
                  >
                    2010 - {CURRENT_YEAR}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFormData({ ...formData, years: [] })}
                    className="text-red-600"
                  >
                    Effacer
                  </Button>
                </div>

                {/* Year grid */}
                <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
                  {YEAR_OPTIONS.map(year => (
                    <button
                      key={year}
                      data-testid={`vehicle-form-year-${year}`}
                      onClick={() => toggleYear(year)}
                      className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                        formData.years.includes(year)
                          ? 'bg-orange-500 text-white shadow-sm'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {year}
                    </button>
                  ))}
                </div>

                {formData.years.length > 0 && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-100">
                    <p className="text-sm text-green-700">
                      <Check size={16} className="inline mr-1" />
                      {formData.years.length} année(s) sélectionnée(s) : 
                      {formData.years.length <= 10 
                        ? ` ${formData.years.join(', ')}`
                        : ` ${Math.min(...formData.years)} - ${Math.max(...formData.years)}`
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Engines */}
          {step === 3 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  <Gauge size={18} />
                  Motorisations
                </label>
                <p className="text-xs text-gray-500 mb-4">
                  Ajoutez les différentes motorisations disponibles pour ce modèle
                </p>

                {/* Period selection */}
                <div className="p-4 bg-gray-50 rounded-lg mb-4">
                  <label className="block text-xs font-medium text-gray-500 mb-2">
                    Période de disponibilité
                  </label>
                  <div className="flex items-center gap-2">
                    <select
                      data-testid="vehicle-form-period-start"
                      value={currentPeriod.start}
                      onChange={(e) => setCurrentPeriod({ ...currentPeriod, start: parseInt(e.target.value) })}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      {YEAR_OPTIONS.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                    <span className="text-gray-400">à</span>
                    <select
                      data-testid="vehicle-form-period-end"
                      value={currentPeriod.end}
                      onChange={(e) => setCurrentPeriod({ ...currentPeriod, end: parseInt(e.target.value) })}
                      className="border rounded-lg px-3 py-2 text-sm"
                    >
                      {YEAR_OPTIONS.map(y => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Engine form */}
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Nom de la motorisation *
                      </label>
                      <Input
                        data-testid="vehicle-form-engine-name"
                        value={currentEngine.name}
                        onChange={(e) => setCurrentEngine({ ...currentEngine, name: e.target.value })}
                        placeholder="Ex: 1.5 dCi 110ch"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Type de carburant
                      </label>
                      <select
                        data-testid="vehicle-form-engine-fuel"
                        value={currentEngine.fuel}
                        onChange={(e) => setCurrentEngine({ ...currentEngine, fuel: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2 text-sm"
                      >
                        {FUEL_TYPES.map(fuel => (
                          <option key={fuel.value} value={fuel.value}>{fuel.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Cylindrée (cm³)
                      </label>
                      <Input
                        type="number"
                        data-testid="vehicle-form-engine-displacement"
                        value={currentEngine.displacement}
                        onChange={(e) => setCurrentEngine({ ...currentEngine, displacement: e.target.value })}
                        placeholder="1598"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Puissance (ch) *
                      </label>
                      <Input
                        type="number"
                        data-testid="vehicle-form-engine-power"
                        value={currentEngine.power}
                        onChange={(e) => handlePowerChange(e.target.value)}
                        placeholder="110"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Puissance (kW)
                      </label>
                      <Input
                        type="number"
                        value={currentEngine.power_kw}
                        onChange={(e) => setCurrentEngine({ ...currentEngine, power_kw: e.target.value })}
                        placeholder="81"
                        className="bg-gray-50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Code moteur
                      </label>
                      <Input
                        data-testid="vehicle-form-engine-code"
                        value={currentEngine.engine_code}
                        onChange={(e) => setCurrentEngine({ ...currentEngine, engine_code: e.target.value })}
                        placeholder="K9K"
                      />
                    </div>
                  </div>

                  <Button
                    data-testid="vehicle-form-add-engine-button"
                    onClick={addEngine}
                    className="w-full bg-orange-500 hover:bg-orange-600"
                  >
                    <Plus size={18} className="mr-2" />
                    Ajouter cette motorisation
                  </Button>
                </div>

                {/* Added engines list */}
                {Object.keys(formData.engines).length > 0 && (
                  <div className="mt-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">
                      Motorisations ajoutées ({getTotalEnginesCount()})
                    </h4>
                    <div className="space-y-3">
                      {Object.entries(formData.engines).map(([period, engines]) => (
                        <div key={period} className="border rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600">
                            Période: {period}
                          </div>
                          <div className="divide-y">
                            {engines.map((engine, idx) => (
                              <div key={idx} className="px-3 py-2 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    FUEL_TYPES.find(f => f.value === engine.fuel)?.color || 'bg-gray-100'
                                  }`}>
                                    {FUEL_TYPES.find(f => f.value === engine.fuel)?.label}
                                  </span>
                                  <span className="font-medium text-sm">{engine.name}</span>
                                  <span className="text-xs text-gray-500">
                                    {engine.displacement > 0 && `${engine.displacement}cm³ • `}
                                    {engine.power}ch
                                  </span>
                                </div>
                                <button
                                  data-testid={`vehicle-form-remove-engine-${period}-${idx}`}
                                  onClick={() => removeEngine(period, idx)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Summary */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Récapitulatif</h3>
                <p className="text-gray-500">Vérifiez les informations avant de valider</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                <div className="flex items-center gap-3 pb-4 border-b">
                  <Car size={24} className="text-orange-500" />
                  <div>
                    <p className="text-xs text-gray-500">Véhicule</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formData.brand.toUpperCase()} {formData.model}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 pb-4 border-b">
                  <Calendar size={24} className="text-orange-500" />
                  <div>
                    <p className="text-xs text-gray-500">Années de production</p>
                    <p className="font-medium text-gray-900">
                      {Math.min(...formData.years)} - {Math.max(...formData.years)}
                      <span className="text-gray-500 ml-2">({formData.years.length} années)</span>
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Gauge size={24} className="text-orange-500" />
                  <div>
                    <p className="text-xs text-gray-500">Motorisations</p>
                    <p className="font-medium text-gray-900">
                      {getTotalEnginesCount()} motorisation(s) sur {Object.keys(formData.engines).length} période(s)
                    </p>
                  </div>
                </div>
              </div>

              {/* Engines summary */}
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 font-medium text-sm">
                  Détail des motorisations
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {Object.entries(formData.engines).map(([period, engines]) => (
                    <div key={period}>
                      <div className="px-4 py-1 bg-gray-100 text-xs text-gray-600">{period}</div>
                      {engines.map((engine, idx) => (
                        <div key={idx} className="px-4 py-2 flex items-center gap-2 border-t">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            FUEL_TYPES.find(f => f.value === engine.fuel)?.color
                          }`}>
                            {FUEL_TYPES.find(f => f.value === engine.fuel)?.label}
                          </span>
                          <span className="text-sm">{engine.name}</span>
                          <span className="text-xs text-gray-400">
                            {engine.power}ch ({engine.power_kw}kW)
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between">
          <Button
            variant="outline"
            data-testid="vehicle-form-back-button"
            onClick={step === (isEdit ? 2 : 1) ? onClose : () => setStep(step - 1)}
          >
            {step === (isEdit ? 2 : 1) ? 'Annuler' : 'Précédent'}
          </Button>
          
          {step < 4 ? (
            <Button
              data-testid="vehicle-form-next-button"
              onClick={handleNext}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Suivant
            </Button>
          ) : (
            <Button
              data-testid="vehicle-form-submit-button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-700"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" />
                  {isEdit ? 'Enregistrement...' : 'Ajout en cours...'}
                </>
              ) : (
                <>
                  <Check size={18} className="mr-2" />
                  {isEdit ? 'Enregistrer les modifications' : 'Ajouter le véhicule'}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehicleAddForm;
